import React, { useEffect } from 'react';
import { useService } from '@aesop-fables/containr-react';
import { useObservable, useSubject } from '@guidedchoice/scrinium';
import { Api, Data, Domain, Hooks } from '@3nickels/data-modules';
import { ContinueLinkingItem } from './Types';
import { deferUntilAfterPaint, getContinueLinkingItems } from './utils';

const ContinueLinkingLoader: React.FC<{
  onItemsChange: React.Dispatch<React.SetStateAction<ContinueLinkingItem[]>>;
  onLoadingChange?: React.Dispatch<React.SetStateAction<boolean>>;
  wizard: ReturnType<typeof Hooks.usePlaidWizard>;
}> = ({ onItemsChange, onLoadingChange, wizard }) => {
  const plaidApi = useService<Api.PlaidApi>(Api.ApiKeys.Plaid);
  const institutions = useObservable(wizard.institutions$) ?? [];
  const combinedHousingAccounts =
    useSubject<Domain.HousingData[]>(Data.RealEstate.HousingEntityServices.CombinedHousingData) ??
    [];

  useEffect(() => {
    if (institutions.length === 0) {
      onItemsChange([]);
      onLoadingChange?.(false);
      return;
    }

    let cancelled = false;

    const loadContinueLinkingItems = async () => {
      const items = await getContinueLinkingItems({
        combinedHousingAccounts,
        institutions,
        plaidApi,
      });

      if (cancelled) {
        return;
      }

      onItemsChange(items);
      onLoadingChange?.(false);
    };

    deferUntilAfterPaint(() => {
      if (cancelled) {
        return;
      }

      onLoadingChange?.(true);
      onItemsChange([]);
      void loadContinueLinkingItems();
    });

    return () => {
      cancelled = true;
    };
  }, [combinedHousingAccounts, institutions, onItemsChange, onLoadingChange, plaidApi]);

  return null;
};

export default ContinueLinkingLoader;
